import {FC, useState} from 'react'
import {
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalTitle,
    ModalOverlay,
    ModalXCloseButton
} from '@/components/ui/modal-atoms/ModalAtoms'
import {Button} from '@/components/ui/button/Button'
import {Text} from '@/components/ui/text/Text'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {useTranslation} from '@/translations/i18n'
import {SendMailPayload} from '@/features/mailer/types'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {Badge} from '@/components/ui/badge/Badge'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {Users01Icon, User01Icon, Mail01Icon} from '@/components/ui/icon/Icon'
import {
    StyledRecipientRow,
    StyledRecipientInfo,
    StyledRecipientName,
    StyledRecipientEmail,
    StyledSectionHeader,
    StyledLoadMoreWrapper,
    StyledScrollContainer,
    StyledSectionBlock,
    StyledEmptyState,
    StyledTabsBar,
    StyledTabButton,
    StyledTabCount
} from './style'
import {usePatientsPreview, useConsultantsPreview} from '@/features/mailer/queries/usePreviewRecipients'

interface MailerRecipientsPreviewModalProps {
    recipients: SendMailPayload['recipients']
    onClose: () => void
}

const SkeletonRow = () => (
    <Flexbox align="center" gap={3} style={{padding: '10px 0', borderBottom: '1px solid #f1f1f1'}}>
        <div style={{width: 32, height: 32, borderRadius: '50%', overflow: 'hidden', flexShrink: 0}}>
            <Skeleton />
        </div>
        <Flexbox direction="column" gap={1.5} style={{flex: 1}}>
            <div style={{height: 13, width: '42%'}}>
                <Skeleton />
            </div>
            <div style={{height: 11, width: '62%'}}>
                <Skeleton />
            </div>
        </Flexbox>
    </Flexbox>
)

const PreviewSkeleton = () => (
    <Flexbox direction="column" fullWidth>
        {Array.from({length: 7}, (_, i) => (
            <SkeletonRow key={i} />
        ))}
    </Flexbox>
)

export const MailerRecipientsPreviewModal: FC<MailerRecipientsPreviewModalProps> = ({recipients, onClose}) => {
    const {t} = useTranslation()

    const patientsQuery = usePatientsPreview(recipients)
    const consultantsQuery = useConsultantsPreview(recipients)

    const patients = patientsQuery.remappedData
    const consultants = consultantsQuery.remappedData
    const patientsTotal = patientsQuery.total
    const consultantsTotal = consultantsQuery.total
    const totalCount = patientsTotal + consultantsTotal

    const hasBothGroups = patientsTotal > 0 && consultantsTotal > 0
    const [activeTab, setActiveTab] = useState<'patients' | 'consultants'>('patients')

    const isLoading = patientsQuery.isLoading || consultantsQuery.isLoading
    const isError = patientsQuery.isError || consultantsQuery.isError
    const hasData = !!(patientsQuery.data || consultantsQuery.data)

    return (
        <>
            <ModalOverlay onClick={onClose} />
            <Modal width="640px">
                <ModalHeader>
                    <Flexbox justify="space-between" align="center" fullWidth>
                        <Flexbox align="center" gap={2}>
                            <ModalTitle>{t('mailer:preview_modal_title')}</ModalTitle>
                            {!isLoading && hasData && (
                                <Badge variant={totalCount > 0 ? 'primary' : 'orange'}>
                                    {t('mailer:recipients_count', {count: totalCount})}
                                </Badge>
                            )}
                        </Flexbox>
                        <ModalXCloseButton onClick={onClose} />
                    </Flexbox>
                </ModalHeader>

                <ModalBody style={{padding: '20px 24px', display: 'flex', flexDirection: 'column'}}>
                    {isLoading ? (
                        <PreviewSkeleton />
                    ) : isError ? (
                        <InputHelpText error={t('errors:default')} />
                    ) : hasData ? (
                        <>
                            {hasBothGroups && (
                                <StyledTabsBar>
                                    <StyledTabButton
                                        type="button"
                                        $active={activeTab === 'patients'}
                                        onClick={() => setActiveTab('patients')}
                                    >
                                        <Users01Icon size={14} />
                                        {t('mailer:preview_patients_label')}
                                        <StyledTabCount $active={activeTab === 'patients'}>
                                            {patientsTotal}
                                        </StyledTabCount>
                                    </StyledTabButton>
                                    <StyledTabButton
                                        type="button"
                                        $active={activeTab === 'consultants'}
                                        onClick={() => setActiveTab('consultants')}
                                    >
                                        <User01Icon size={14} />
                                        {t('mailer:preview_consultants_label')}
                                        <StyledTabCount $active={activeTab === 'consultants'}>
                                            {consultantsTotal}
                                        </StyledTabCount>
                                    </StyledTabButton>
                                </StyledTabsBar>
                            )}
                            <StyledScrollContainer>
                                {(!hasBothGroups || activeTab === 'patients') && patientsTotal > 0 && (
                                    <StyledSectionBlock>
                                        {!hasBothGroups && (
                                            <StyledSectionHeader>
                                                <Users01Icon size={14} />
                                                {t('mailer:preview_patients_title', {count: patientsTotal})}
                                            </StyledSectionHeader>
                                        )}

                                        {patients.map(patient => (
                                            <StyledRecipientRow key={patient.id}>
                                                <Avatar name={patient.fullName} size="sm" />
                                                <StyledRecipientInfo>
                                                    <StyledRecipientName>{patient.fullName}</StyledRecipientName>
                                                    <StyledRecipientEmail>{patient.email}</StyledRecipientEmail>
                                                </StyledRecipientInfo>
                                            </StyledRecipientRow>
                                        ))}

                                        {patientsQuery.hasNextPage && (
                                            <StyledLoadMoreWrapper>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => patientsQuery.fetchNextPage()}
                                                    disabled={patientsQuery.isFetchingNextPage}
                                                >
                                                    {patientsQuery.isFetchingNextPage ? (
                                                        <Spinner size={14} />
                                                    ) : (
                                                        t('mailer:load_more')
                                                    )}
                                                </Button>
                                            </StyledLoadMoreWrapper>
                                        )}
                                    </StyledSectionBlock>
                                )}

                                {(!hasBothGroups || activeTab === 'consultants') && consultantsTotal > 0 && (
                                    <StyledSectionBlock>
                                        {!hasBothGroups && (
                                            <StyledSectionHeader>
                                                <User01Icon size={14} />
                                                {t('mailer:preview_consultants_title', {count: consultantsTotal})}
                                            </StyledSectionHeader>
                                        )}

                                        {consultants.map(consultant => (
                                            <StyledRecipientRow key={consultant.id}>
                                                <Avatar name={consultant.fullName} size="sm" />
                                                <StyledRecipientInfo>
                                                    <StyledRecipientName>{consultant.fullName}</StyledRecipientName>
                                                    <StyledRecipientEmail>{consultant.email}</StyledRecipientEmail>
                                                </StyledRecipientInfo>
                                            </StyledRecipientRow>
                                        ))}

                                        {consultantsQuery.hasNextPage && (
                                            <StyledLoadMoreWrapper>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => consultantsQuery.fetchNextPage()}
                                                    disabled={consultantsQuery.isFetchingNextPage}
                                                >
                                                    {consultantsQuery.isFetchingNextPage ? (
                                                        <Spinner size={14} />
                                                    ) : (
                                                        t('mailer:load_more')
                                                    )}
                                                </Button>
                                            </StyledLoadMoreWrapper>
                                        )}
                                    </StyledSectionBlock>
                                )}

                                {totalCount === 0 && (
                                    <StyledEmptyState>
                                        <Mail01Icon size={32} />
                                        <Text size="textSm">{t('mailer:no_recipients')}</Text>
                                    </StyledEmptyState>
                                )}
                            </StyledScrollContainer>
                        </>
                    ) : null}
                </ModalBody>

                <ModalFooter>
                    <Button variant="ghost" onClick={onClose} type="button">
                        {t('commons:close')}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    )
}
